import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { CalendarClock, Flag, Maximize, Trophy, Zap } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getDb } from "@/db";
import {
	cars,
	events,
	heatAssignments,
	heats,
	laneRecords,
	runs,
} from "@/db/schema";
import { getLiveState, type LiveScene } from "@/lib/live-state";
import { computeScores, formatScore } from "@/lib/scoring";

// ── Server functions ─────────────────────────────────────────────────

const getEventWithData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		const eventRuns = await db
			.select()
			.from(runs)
			.where(and(eq(runs.eventId, event.id), eq(runs.status, "valid")));

		const records = await db
			.select()
			.from(laneRecords)
			.where(eq(laneRecords.eventId, event.id));

		const eventHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, event.id));
		const allAssignments = [];
		for (const heat of eventHeats) {
			const ha = await db
				.select()
				.from(heatAssignments)
				.where(eq(heatAssignments.heatId, heat.id));
			allAssignments.push(...ha);
		}

		return {
			event,
			cars: eventCars,
			runs: eventRuns,
			laneRecords: records,
			heats: eventHeats,
			assignments: allAssignments,
		};
	});

// ── Route ────────────────────────────────────────────────────────────

export const Route = createFileRoute("/events/$eventId/tv")({
	loader: ({ params }) =>
		Promise.all([
			getEventWithData({ data: params.eventId }),
			getLiveState({ data: params.eventId }),
		]).then(([data, live]) => ({ ...data, liveState: live })),
	component: TVDisplay,
});

// ── Helpers ──────────────────────────────────────────────────────────

function formatTime(ms: number | null | undefined): string {
	if (ms == null) return "--";
	return `${(ms / 1000).toFixed(3)}s`;
}

type CarData = {
	id: number;
	carName: string | null;
	driverName: string;
	carNumber: number;
	avatarUrl: string | null;
	photoUrl: string | null;
	division: string | null;
};

// ── Sound effects ────────────────────────────────────────────────────

function useSoundEffects() {
	const ctxRef = useRef<AudioContext | null>(null);

	const getCtx = useCallback(() => {
		if (!ctxRef.current) {
			ctxRef.current = new AudioContext();
		}
		return ctxRef.current;
	}, []);

	const playTone = useCallback(
		(freq: number, duration: number, type: OscillatorType = "sine") => {
			try {
				const ctx = getCtx();
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = type;
				osc.frequency.value = freq;
				gain.gain.setValueAtTime(0.15, ctx.currentTime);
				gain.gain.exponentialRampToValueAtTime(
					0.001,
					ctx.currentTime + duration,
				);
				osc.connect(gain);
				gain.connect(ctx.destination);
				osc.start();
				osc.stop(ctx.currentTime + duration);
			} catch {
				// Audio not available
			}
		},
		[getCtx],
	);

	const playStart = useCallback(() => {
		// Quick ascending beep
		playTone(440, 0.15);
		setTimeout(() => playTone(660, 0.15), 100);
		setTimeout(() => playTone(880, 0.25), 200);
	}, [playTone]);

	const playFinish = useCallback(() => {
		// Single ding
		playTone(1200, 0.3, "triangle");
	}, [playTone]);

	const playRecord = useCallback(() => {
		// Fanfare
		playTone(523, 0.15, "square");
		setTimeout(() => playTone(659, 0.15, "square"), 120);
		setTimeout(() => playTone(784, 0.15, "square"), 240);
		setTimeout(() => playTone(1047, 0.4, "square"), 360);
	}, [playTone]);

	return { playStart, playFinish, playRecord };
}

// ── Main component ───────────────────────────────────────────────────

function TVDisplay() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();
	const sounds = useSoundEffects();

	// Poll leaderboard data every 5s
	const { data } = useQuery({
		queryKey: ["event-tv", params.eventId],
		queryFn: () => getEventWithData({ data: params.eventId }),
		initialData,
		refetchInterval: 5000,
	});

	// Poll live state every 1s
	const { data: liveScene } = useQuery({
		queryKey: ["event-tv-live", params.eventId],
		queryFn: () => getLiveState({ data: params.eventId }),
		initialData: initialData.liveState,
		refetchInterval: 1000,
	});

	const {
		event,
		cars: eventCars,
		runs: eventRuns,
		laneRecords: records,
		heats: eventHeats,
		assignments: eventAssignments,
	} = data;
	const divisions: string[] = (() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	})();

	const searchParams = new URLSearchParams(
		typeof window !== "undefined" ? window.location.search : "",
	);
	const autoRotate = searchParams.get("rotate") !== "false";

	// Auto-transition from results to upcoming after 10s
	const [forceIdle, setForceIdle] = useState(false);
	const resultsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastSceneRef = useRef<string>("upcoming");
	const prevSceneRef = useRef<string>("upcoming");

	// Sound effects on scene transitions
	useEffect(() => {
		const scene = liveScene?.scene ?? "upcoming";
		const prev = prevSceneRef.current;

		if (scene === "racing" && prev !== "racing") {
			sounds.playStart();
		}
		if (scene === "results" && prev !== "results") {
			sounds.playFinish();
			// Check for new records
			if (
				liveScene?.scene === "results" &&
				liveScene.newRecords &&
				liveScene.newRecords.length > 0
			) {
				setTimeout(() => sounds.playRecord(), 800);
			}
		}
		prevSceneRef.current = scene;
	}, [liveScene, sounds]);

	useEffect(() => {
		const scene = liveScene?.scene ?? "upcoming";
		if (scene === "results" && lastSceneRef.current !== "results") {
			resultsTimerRef.current = setTimeout(
				() => {
					setForceIdle(true);
				},
				autoRotate ? 10_000 : 30_000,
			);
		}
		if (scene !== "results") {
			setForceIdle(false);
			if (resultsTimerRef.current) {
				clearTimeout(resultsTimerRef.current);
				resultsTimerRef.current = null;
			}
		}
		lastSceneRef.current = scene;
	}, [liveScene?.scene, autoRotate]);

	// Auto-recover from stale racing state (e.g., final push was missed)
	const racingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		if (racingTimerRef.current) {
			clearTimeout(racingTimerRef.current);
			racingTimerRef.current = null;
		}

		if (liveScene?.scene === "racing") {
			const age = Date.now() - liveScene.startedAt;
			const remaining = 60_000 - age;
			if (remaining <= 0) {
				setForceIdle(true);
			} else {
				racingTimerRef.current = setTimeout(() => {
					setForceIdle(true);
				}, remaining);
			}
		}
	}, [liveScene]);

	useEffect(() => {
		return () => {
			if (resultsTimerRef.current) clearTimeout(resultsTimerRef.current);
			if (racingTimerRef.current) clearTimeout(racingTimerRef.current);
		};
	}, []);

	// Scene transition animation
	const [sceneVisible, setSceneVisible] = useState(true);
	const activeScene =
		forceIdle || !liveScene ? "upcoming" : liveScene.scene;
	const activeSceneRef = useRef(activeScene);

	useEffect(() => {
		if (activeScene !== activeSceneRef.current) {
			// Fade out, swap, fade in
			setSceneVisible(false);
			const timer = setTimeout(() => {
				activeSceneRef.current = activeScene;
				setSceneVisible(true);
			}, 300);
			return () => clearTimeout(timer);
		}
	}, [activeScene]);

	const displayedScene = activeSceneRef.current;

	// Fullscreen toggle
	const handleFullscreen = useCallback(() => {
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else {
			document.documentElement.requestFullscreen();
		}
	}, []);

	return (
		<div className="fixed inset-0 z-50 bg-background overflow-hidden flex flex-col">
			<style>{`
				@keyframes slideUp {
					from { opacity: 0; transform: translateY(40px); }
					to { opacity: 1; transform: translateY(0); }
				}
				@keyframes fadeIn {
					from { opacity: 0; }
					to { opacity: 1; }
				}
				@keyframes fadeOut {
					from { opacity: 1; }
					to { opacity: 0; }
				}
				@keyframes pulse-border {
					0%, 100% { border-color: hsl(var(--cherry)); }
					50% { border-color: transparent; }
				}
				@keyframes sparkle {
					0%, 100% { opacity: 1; transform: scale(1); }
					50% { opacity: 0.7; transform: scale(1.1); }
				}
				@keyframes slideInFromBottom {
					from { opacity: 0; transform: translateY(60px); }
					to { opacity: 1; transform: translateY(0); }
				}
				.scene-transition {
					transition: opacity 0.3s ease-in-out;
				}
			`}</style>

			{/* Header */}
			<TVHeader
				event={event}
				activeScene={displayedScene}
				liveScene={liveScene}
				totalHeats={
					eventHeats.filter(
						(h) =>
							h.phase === (event.status === "finals" ? "finals" : "qualifying"),
					).length
				}
				onFullscreen={handleFullscreen}
			/>

			{/* Scene content */}
			<div
				className="flex-1 p-6 overflow-auto scene-transition"
				style={{ opacity: sceneVisible ? 1 : 0 }}
			>
				{displayedScene === "upcoming" && (
					<UpcomingScene
						event={event}
						heats={eventHeats}
						assignments={eventAssignments}
						cars={eventCars}
					/>
				)}
				{displayedScene === "leaderboard" && (
					<LeaderboardScene
						event={event}
						cars={eventCars}
						runs={eventRuns}
						records={records}
						divisions={divisions}
						autoRotate={autoRotate}
						heats={eventHeats}
						assignments={eventAssignments}
						isFinals={event.status === "finals"}
					/>
				)}
				{displayedScene === "lineup" && liveScene?.scene === "lineup" && (
					<LineupScene liveScene={liveScene} cars={eventCars} />
				)}
				{displayedScene === "racing" && liveScene?.scene === "racing" && (
					<RacingScene liveScene={liveScene} cars={eventCars} />
				)}
				{displayedScene === "results" && liveScene?.scene === "results" && (
					<ResultsScene liveScene={liveScene} cars={eventCars} />
				)}
				{displayedScene === "awards" && <AwardsScene />}
				{displayedScene === "announcement" &&
					liveScene?.scene === "announcement" && (
						<AnnouncementScene liveScene={liveScene} />
					)}
			</div>
		</div>
	);
}

// ── Header ───────────────────────────────────────────────────────────

function TVHeader({
	event,
	activeScene,
	liveScene,
	totalHeats,
	onFullscreen,
}: {
	event: {
		name: string;
		date: string;
		location: string | null;
		status: string;
		imageUrl: string | null;
	};
	activeScene: string;
	liveScene: LiveScene | null;
	totalHeats: number;
	onFullscreen: () => void;
}) {
	const isLive = event.status === "qualifying" || event.status === "finals";
	const heat =
		liveScene && "heat" in liveScene
			? (liveScene as { heat: number }).heat
			: null;
	const roundLabel = event.status === "finals" ? "Finals" : "Qualifying";

	return (
		<div className="flex items-center justify-between px-6 pt-5 pb-3">
			<div className="flex items-center gap-4">
				{event.imageUrl ? (
					<img
						src={event.imageUrl}
						alt={event.name}
						className="h-14 w-auto rounded-lg object-cover"
					/>
				) : (
					<Flag size={32} className="text-cherry" />
				)}
				<div>
					<h1 className="text-3xl text-foreground font-bold">{event.name}</h1>
					<p className="text-muted-foreground text-sm">
						{event.date}
						{event.location && ` \u2022 ${event.location}`}
					</p>
				</div>
			</div>
			<div className="flex items-center gap-3">
				{heat != null && activeScene !== "leaderboard" && activeScene !== "upcoming" && (
					<span className="px-3 py-1 bg-muted text-foreground text-sm font-bold rounded-full">
						{roundLabel} &mdash; Heat {heat}
						{totalHeats > 0 && ` of ${totalHeats}`}
					</span>
				)}
				{isLive && (
					<span className="flex items-center gap-2 px-3 py-1 bg-success text-foreground text-sm font-bold uppercase rounded-full animate-pulse">
						<span className="w-2 h-2 bg-success/50 rounded-full" />
						Live
					</span>
				)}
				<button
					type="button"
					onClick={onFullscreen}
					className="p-2 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
					title="Toggle fullscreen"
				>
					<Maximize size={18} />
				</button>
			</div>
		</div>
	);
}

// ── Leaderboard Scene ────────────────────────────────────────────────

function LeaderboardScene({
	event,
	cars: eventCars,
	runs: eventRuns,
	records,
	divisions,
	autoRotate,
	heats: eventHeats,
	assignments: eventAssignments,
	isFinals,
}: {
	event: {
		scoringMethod: string;
		finalsScoringMethod: string | null;
		attemptsAllowed: number;
		laneCount: number;
	};
	cars: CarData[];
	runs: Array<{
		carId: number;
		elapsedTimeMs: number | null;
		lane: number | null;
		heatId: number | null;
	}>;
	records: Array<{
		lane: number;
		carId: number;
		elapsedTimeMs: number;
		division: string | null;
	}>;
	divisions: string[];
	autoRotate: boolean;
	heats: Array<{ id: number; status: string; phase: string }>;
	assignments: Array<{ heatId: number; lane: number; carId: number }>;
	isFinals: boolean;
}) {
	const [activeDivision, setActiveDivision] = useState<string | null>(null);

	// Auto-cycle division tabs every 15s
	useEffect(() => {
		if (!autoRotate || divisions.length === 0) return;
		let cycleIndex = 0;
		const interval = setInterval(() => {
			cycleIndex = (cycleIndex + 1) % (divisions.length + 1);
			setActiveDivision(cycleIndex === 0 ? null : divisions[cycleIndex - 1]);
		}, 15_000);
		return () => clearInterval(interval);
	}, [autoRotate, divisions]);

	const filteredCars = activeDivision
		? eventCars.filter((c) => c.division === activeDivision)
		: eventCars;

	const scoringMethod = isFinals
		? event.finalsScoringMethod || event.scoringMethod
		: event.scoringMethod;

	const scores = computeScores(
		filteredCars.map((c) => ({
			id: c.id,
			carNumber: c.carNumber,
			carName: c.carName,
			driverName: c.driverName,
			avatarUrl: c.avatarUrl,
			photoUrl: c.photoUrl,
			division: c.division,
		})),
		eventRuns.map((r) => ({
			carId: r.carId,
			elapsedTimeMs: r.elapsedTimeMs,
			lane: r.lane ?? 0,
			heatId: r.heatId,
		})),
		scoringMethod,
		{
			heats: eventHeats.map((h) => ({
				id: h.id,
				status: h.status,
				phase: h.phase,
			})),
			assignments: eventAssignments.map((a) => ({
				heatId: a.heatId,
				lane: a.lane,
				carId: a.carId,
			})),
			laneCount: event.laneCount,
			phase: isFinals ? "finals" : undefined,
		},
	);

	// Show division-specific records when filtering, otherwise overall
	const displayRecords = activeDivision
		? records.filter((r) => r.division === activeDivision)
		: records.filter((r) => r.division == null);

	const carCount = scores.length;
	const gridCols =
		carCount <= 3
			? "grid-cols-1 sm:grid-cols-3"
			: carCount <= 4
				? "grid-cols-2"
				: carCount <= 6
					? "grid-cols-2 lg:grid-cols-3"
					: carCount <= 9
						? "grid-cols-3"
						: "grid-cols-3 lg:grid-cols-4";

	return (
		<div className="flex flex-col h-full">
			{/* Division tabs */}
			{divisions.length > 0 && (
				<div className="flex gap-2 mb-4 justify-center">
					<button
						type="button"
						onClick={() => setActiveDivision(null)}
						className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
							activeDivision == null
								? "bg-primary text-primary-foreground"
								: "bg-muted text-muted-foreground hover:text-foreground"
						}`}
					>
						All
					</button>
					{divisions.map((div) => (
						<button
							key={div}
							type="button"
							onClick={() => setActiveDivision(div)}
							className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
								activeDivision === div
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:text-foreground"
							}`}
						>
							{div}
						</button>
					))}
				</div>
			)}

			{/* Grid */}
			<div className="flex-1">
				<div className={`grid ${gridCols} gap-3 max-w-7xl mx-auto`}>
					{scores.map((entry, i) => {
						const podiumBorder =
							i === 0
								? "border-gold/60"
								: i === 1
									? "border-silver/50"
									: i === 2
										? "border-bronze/50"
										: "border-border";
						const podiumBg =
							i === 0
								? "bg-gold/15"
								: i === 1
									? "bg-silver/10"
									: i === 2
										? "bg-bronze/10"
										: "bg-card/50";

						return (
							<div
								key={entry.car.id}
								className={`relative rounded-xl border-2 ${podiumBorder} ${podiumBg} p-4 flex flex-col items-center text-center transition-all`}
							>
								<span
									className={`absolute -top-3 -left-3 inline-flex items-center justify-center w-10 h-10 rounded-full font-black text-lg shadow-md ${
										i === 0
											? "bg-gold text-primary-foreground"
											: i === 1
												? "bg-silver text-primary-foreground"
												: i === 2
													? "bg-bronze text-foreground"
													: "bg-muted text-muted-foreground"
									}`}
								>
									{i + 1}
								</span>

								{entry.car.photoUrl ? (
									<img
										src={entry.car.photoUrl}
										alt=""
										className="w-24 h-24 rounded-xl object-cover mb-3"
									/>
								) : entry.car.avatarUrl ? (
									<img
										src={entry.car.avatarUrl}
										alt=""
										className="w-24 h-24 rounded-xl object-cover mb-3"
									/>
								) : (
									<div className="w-24 h-24 rounded-xl bg-muted flex items-center justify-center text-muted-foreground font-bold text-3xl mb-3">
										{entry.car.driverName.charAt(0)}
									</div>
								)}

								<p className="text-foreground font-black text-lg truncate w-full mb-2">
									<span className="inline-flex items-center text-xs font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mr-1.5">
										#{entry.car.carNumber}
									</span>
									{entry.car.driverName}
								</p>

								<p className="text-primary font-display font-black text-3xl tabular-nums">
									{formatScore(entry)}
								</p>
								<p className="text-muted-foreground text-xs">
									{entry.runCount}/{event.attemptsAllowed} runs
								</p>
							</div>
						);
					})}
				</div>
			</div>

			{/* Lane records footer — always show lane slots */}
			<div className="mt-4 border-t border-border pt-3">
				<div className="flex items-center justify-center gap-6 text-sm">
					<span className="text-muted-foreground font-bold uppercase text-xs">
						{activeDivision ? `${activeDivision} Records` : "Lane Records"}
					</span>
					{Array.from({ length: event.laneCount }, (_, i) => i + 1).map(
						(lane) => {
							const record = displayRecords.find((r) => r.lane === lane);
							const car = record
								? eventCars.find((c) => c.id === record.carId)
								: null;
							return (
								<div key={lane} className="text-center">
									<span className="text-muted-foreground text-xs">L{lane}</span>
									<p className="text-foreground font-display font-bold tabular-nums">
										{record ? formatTime(record.elapsedTimeMs) : "--"}
									</p>
									{car && (
										<p className="text-muted-foreground text-xs truncate max-w-[80px]">
											{car.driverName}
										</p>
									)}
								</div>
							);
						},
					)}
				</div>
			</div>
		</div>
	);
}

// ── Upcoming Scene ───────────────────────────────────────────────────

function UpcomingScene({
	event,
	heats: eventHeats,
	assignments: eventAssignments,
	cars: eventCars,
}: {
	event: { status: string };
	heats: Array<{
		id: number;
		heatNumber: number;
		status: string;
		phase: string;
	}>;
	assignments: Array<{ heatId: number; lane: number; carId: number }>;
	cars: CarData[];
}) {
	const phase = event.status === "finals" ? "finals" : "qualifying";
	const scheduled = eventHeats
		.filter((h) => h.phase === phase && h.status === "scheduled")
		.sort((a, b) => a.heatNumber - b.heatNumber);
	const currentHeat = scheduled[0];
	const onDeckHeat = scheduled[1];

	if (!currentHeat) {
		// All heats complete
		return (
			<div className="flex items-center justify-center h-full">
				<div className="text-center">
					<Trophy size={64} className="text-gold mx-auto mb-6" />
					<h2 className="text-5xl text-foreground font-black mb-3">
						Racing Complete
					</h2>
					<p className="text-muted-foreground text-xl">
						Standings coming soon
					</p>
				</div>
			</div>
		);
	}

	const currentAssignments = eventAssignments
		.filter((a) => a.heatId === currentHeat.id)
		.sort((a, b) => a.lane - b.lane);
	const onDeckAssignments = onDeckHeat
		? eventAssignments
				.filter((a) => a.heatId === onDeckHeat.id)
				.sort((a, b) => a.lane - b.lane)
		: [];

	return (
		<div className="flex flex-col items-center justify-center h-full gap-10">
			{/* Up Next */}
			<div className="w-full max-w-6xl">
				<div className="flex items-center gap-3 mb-4 justify-center">
					<CalendarClock size={24} className="text-cherry" />
					<h2 className="text-3xl text-foreground font-black uppercase">
						Up Next &mdash; Heat {currentHeat.heatNumber}
					</h2>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
					{currentAssignments.map((entry, i) => {
						const car = eventCars.find((c) => c.id === entry.carId);
						if (!car) return null;
						return (
							<div
								key={entry.lane}
								className="bg-card rounded-2xl border-2 border-border p-6 flex flex-col items-center text-center"
								style={{
									animation: `slideInFromBottom 0.5s ease-out ${i * 0.1}s both`,
								}}
							>
								<span className="text-muted-foreground font-bold uppercase text-xs mb-3">
									Lane {entry.lane}
								</span>
								{car.photoUrl ? (
									<img
										src={car.photoUrl}
										alt=""
										className="w-28 h-28 rounded-xl object-cover mb-4"
									/>
								) : car.avatarUrl ? (
									<img
										src={car.avatarUrl}
										alt=""
										className="w-28 h-28 rounded-xl object-cover mb-4"
									/>
								) : (
									<div className="w-28 h-28 rounded-xl bg-muted flex items-center justify-center text-muted-foreground font-bold text-4xl mb-4">
										{car.driverName.charAt(0)}
									</div>
								)}
								<p className="text-foreground font-black text-xl mb-1">
									<span className="text-sm font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mr-1.5">
										#{car.carNumber}
									</span>
									{car.driverName}
								</p>
								{car.division && (
									<span className="mt-1 inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
										{car.division}
									</span>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* On Deck */}
			{onDeckHeat && onDeckAssignments.length > 0 && (
				<div className="w-full max-w-5xl opacity-60">
					<h3 className="text-lg text-muted-foreground font-bold uppercase text-center mb-3">
						On Deck &mdash; Heat {onDeckHeat.heatNumber}
					</h3>
					<div className="flex justify-center gap-4 flex-wrap">
						{onDeckAssignments.map((entry) => {
							const car = eventCars.find((c) => c.id === entry.carId);
							if (!car) return null;
							return (
								<div
									key={entry.lane}
									className="bg-card/50 rounded-xl border border-border px-4 py-3 flex items-center gap-3"
								>
									<span className="text-muted-foreground font-bold text-xs">
										L{entry.lane}
									</span>
									{car.photoUrl ? (
										<img
											src={car.photoUrl}
											alt=""
											className="w-10 h-10 rounded-lg object-cover"
										/>
									) : car.avatarUrl ? (
										<img
											src={car.avatarUrl}
											alt=""
											className="w-10 h-10 rounded-lg object-cover"
										/>
									) : (
										<div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground font-bold text-sm">
											{car.driverName.charAt(0)}
										</div>
									)}
									<div className="text-left">
										<p className="text-foreground font-bold text-sm truncate max-w-[120px]">
											{car.driverName}
										</p>
										{car.division && (
											<span className="inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
												{car.division}
											</span>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}

// ── Lineup Scene ─────────────────────────────────────────────────────

function LineupScene({
	liveScene,
	cars: eventCars,
}: {
	liveScene: LiveScene & { scene: "lineup" };
	cars: CarData[];
}) {
	return (
		<div className="flex items-center justify-center h-full">
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl w-full">
				{liveScene.lanes.map((entry, i) => {
					const car = eventCars.find((c) => c.id === entry.carId);
					if (!car) return null;
					return (
						<div
							key={entry.lane}
							className="bg-card rounded-2xl border-2 border-border p-6 flex flex-col items-center text-center"
							style={{
								animation: `slideInFromBottom 0.5s ease-out ${i * 0.1}s both`,
							}}
						>
							<span className="text-muted-foreground font-bold uppercase text-xs mb-3">
								Lane {entry.lane}
							</span>
							{car.photoUrl ? (
								<img
									src={car.photoUrl}
									alt=""
									className="w-28 h-28 rounded-xl object-cover mb-4"
								/>
							) : car.avatarUrl ? (
								<img
									src={car.avatarUrl}
									alt=""
									className="w-28 h-28 rounded-xl object-cover mb-4"
								/>
							) : (
								<div className="w-28 h-28 rounded-xl bg-muted flex items-center justify-center text-muted-foreground font-bold text-4xl mb-4">
									{car.driverName.charAt(0)}
								</div>
							)}
							<p className="text-foreground font-black text-xl mb-1">
								<span className="text-sm font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mr-1.5">
									#{car.carNumber}
								</span>
								{car.driverName}
							</p>
							{car.division && (
								<span className="mt-1 inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
									{car.division}
								</span>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

// ── Racing Scene ─────────────────────────────────────────────────────

function RacingScene({
	liveScene,
	cars: eventCars,
}: {
	liveScene: LiveScene & { scene: "racing" };
	cars: CarData[];
}) {
	const allFinished = liveScene.lanes.every(
		(l) => l.status === "valid" || l.status === "dnf",
	);

	// Track race start using a local reference point to avoid clock skew
	// between admin browser (which sets startedAt) and TV browser.
	const localStartRef = useRef<{ remoteTs: number; localTs: number } | null>(
		null,
	);
	if (
		!localStartRef.current ||
		localStartRef.current.remoteTs !== liveScene.startedAt
	) {
		localStartRef.current = {
			remoteTs: liveScene.startedAt,
			localTs: Date.now(),
		};
	}

	const getElapsed = useCallback(() => {
		if (!localStartRef.current) return 0;
		// Estimate elapsed from how long ago we first saw this startedAt
		return Math.max(0, Date.now() - localStartRef.current.localTs);
	}, []);

	const [elapsed, setElapsed] = useState(getElapsed);
	const frozenElapsedRef = useRef<number | null>(null);

	// Freeze timer when all lanes finish
	useEffect(() => {
		if (allFinished && frozenElapsedRef.current == null) {
			frozenElapsedRef.current = getElapsed();
			setElapsed(frozenElapsedRef.current);
		}
	}, [allFinished, getElapsed]);

	// Running timer (only while lanes are still racing)
	useEffect(() => {
		if (allFinished) return;
		frozenElapsedRef.current = null;
		const interval = setInterval(() => {
			setElapsed(getElapsed());
		}, 16);
		return () => clearInterval(interval);
	}, [allFinished, getElapsed]);

	// Sort lanes: finished first (by time), then pending
	const sortedLanes = [...liveScene.lanes].sort((a, b) => {
		const aFinished = a.status === "valid" || a.status === "dnf";
		const bFinished = b.status === "valid" || b.status === "dnf";
		if (aFinished && !bFinished) return -1;
		if (!aFinished && bFinished) return 1;
		if (aFinished && bFinished) {
			const aMs = a.elapsedTimeMs ?? Number.MAX_SAFE_INTEGER;
			const bMs = b.elapsedTimeMs ?? Number.MAX_SAFE_INTEGER;
			return aMs - bMs;
		}
		return a.lane - b.lane;
	});

	const firstFinisher = sortedLanes.find(
		(l) => l.status === "valid" && l.elapsedTimeMs != null,
	);

	return (
		<div className="flex items-center justify-center h-full">
			<div className="max-w-5xl w-full space-y-4">
				{/* Master timer */}
				<div className="text-center mb-6">
					<p className="text-muted-foreground font-bold uppercase text-xs mb-1">
						Race Time
					</p>
					<p className="text-foreground font-display font-black text-6xl tabular-nums">
						{(elapsed / 1000).toFixed(2)}s
					</p>
				</div>

				{/* Lane cards */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					{sortedLanes.map((entry) => {
						const car = eventCars.find((c) => c.id === entry.carId);
						if (!car) return null;
						const finished = entry.status === "valid" || entry.status === "dnf";
						const isWinner =
							firstFinisher &&
							entry.carId === firstFinisher.carId &&
							entry.lane === firstFinisher.lane;

						return (
							<div
								key={entry.lane}
								className={`rounded-2xl border-2 p-5 flex flex-col items-center text-center transition-all ${
									finished
										? isWinner
											? "border-gold bg-gold/15"
											: "border-success/50 bg-success/10"
										: "border-cherry/40 bg-card"
								}`}
								style={
									!finished
										? {
												animation: "pulse-border 1.5s ease-in-out infinite",
											}
										: undefined
								}
							>
								<span className="text-muted-foreground font-bold uppercase text-xs mb-2">
									Lane {entry.lane}
								</span>
								{car.photoUrl ? (
									<img
										src={car.photoUrl}
										alt=""
										className="w-20 h-20 rounded-xl object-cover mb-3"
									/>
								) : car.avatarUrl ? (
									<img
										src={car.avatarUrl}
										alt=""
										className="w-20 h-20 rounded-xl object-cover mb-3"
									/>
								) : (
									<div className="w-20 h-20 rounded-xl bg-muted flex items-center justify-center text-muted-foreground font-bold text-3xl mb-3">
										{car.driverName.charAt(0)}
									</div>
								)}
								<p className="text-foreground font-black text-lg mb-1 truncate w-full">
									{car.driverName}
								</p>
								<p className="font-display font-black text-4xl tabular-nums mt-2">
									{entry.status === "dnf" ? (
										<span className="text-destructive">DNF</span>
									) : finished ? (
										<span className={isWinner ? "text-gold" : "text-success"}>
											{formatTime(entry.elapsedTimeMs)}
										</span>
									) : (
										<span className="text-cherry">
											{(elapsed / 1000).toFixed(2)}s
										</span>
									)}
								</p>
								{isWinner && (
									<Trophy
										size={20}
										className="text-gold mt-2"
										style={{
											animation: "sparkle 1s ease-in-out infinite",
										}}
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}

// ── Results Scene ────────────────────────────────────────────────────

function ResultsScene({
	liveScene,
	cars: eventCars,
}: {
	liveScene: LiveScene & { scene: "results" };
	cars: CarData[];
}) {
	// Sort by time (winner first), DNFs last
	const sortedLanes = [...liveScene.lanes].sort((a, b) => {
		if (a.status === "dnf" && b.status !== "dnf") return 1;
		if (a.status !== "dnf" && b.status === "dnf") return -1;
		const aMs = a.elapsedTimeMs ?? Number.MAX_SAFE_INTEGER;
		const bMs = b.elapsedTimeMs ?? Number.MAX_SAFE_INTEGER;
		return aMs - bMs;
	});

	const newRecordLanes = new Set(
		(liveScene.newRecords ?? []).map((r) => r.lane),
	);

	return (
		<div className="flex items-center justify-center h-full">
			<div className="max-w-5xl w-full">
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
					{sortedLanes.map((entry, i) => {
						const car = eventCars.find((c) => c.id === entry.carId);
						if (!car) return null;
						const isWinner = i === 0 && entry.status !== "dnf";
						const isDnf = entry.status === "dnf";
						const hasNewRecord = newRecordLanes.has(entry.lane);

						const placementColor = isWinner
							? "border-gold bg-gold/15"
							: i === 1
								? "border-silver/60 bg-silver/10"
								: i === 2
									? "border-bronze/60 bg-bronze/10"
									: isDnf
										? "border-destructive/50 bg-destructive/10"
										: "border-border bg-card/50";

						const placementBadge = isWinner
							? "bg-gold text-primary-foreground"
							: i === 1
								? "bg-silver text-primary-foreground"
								: i === 2
									? "bg-bronze text-foreground"
									: "bg-muted text-muted-foreground";

						return (
							<div
								key={entry.lane}
								className={`relative rounded-2xl border-2 ${placementColor} p-6 flex flex-col items-center text-center`}
								style={{
									animation: `slideInFromBottom 0.5s ease-out ${i * 0.1}s both`,
								}}
							>
								{/* Placement badge */}
								<span
									className={`absolute -top-3 -left-3 inline-flex items-center justify-center w-10 h-10 rounded-full font-black text-lg shadow-md ${placementBadge}`}
								>
									{isDnf ? "X" : i + 1}
								</span>

								{/* New record badge */}
								{hasNewRecord && (
									<div
										className="absolute -top-2 -right-2 px-2 py-0.5 bg-cherry text-foreground text-xs font-black uppercase rounded-full shadow-lg flex items-center gap-1"
										style={{
											animation: "sparkle 1.5s ease-in-out infinite",
										}}
									>
										<Zap size={10} />
										Record
									</div>
								)}

								<span className="text-muted-foreground font-bold uppercase text-xs mb-3">
									Lane {entry.lane}
								</span>

								{car.photoUrl ? (
									<img
										src={car.photoUrl}
										alt=""
										className={`rounded-xl object-cover mb-3 ${isWinner ? "w-28 h-28" : "w-24 h-24"}`}
									/>
								) : car.avatarUrl ? (
									<img
										src={car.avatarUrl}
										alt=""
										className={`rounded-xl object-cover mb-3 ${isWinner ? "w-28 h-28" : "w-24 h-24"}`}
									/>
								) : (
									<div
										className={`rounded-xl bg-muted flex items-center justify-center text-muted-foreground font-bold mb-3 ${isWinner ? "w-28 h-28 text-4xl" : "w-24 h-24 text-3xl"}`}
									>
										{car.driverName.charAt(0)}
									</div>
								)}

								<p className="text-foreground font-black text-xl mb-1 truncate w-full">
									<span className="text-sm font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mr-1.5">
										#{car.carNumber}
									</span>
									{car.driverName}
								</p>
								{car.division && (
									<span className="mb-2 inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
										{car.division}
									</span>
								)}

								<p
									className={`font-display font-black tabular-nums ${
										isWinner
											? "text-5xl text-gold"
											: isDnf
												? "text-4xl text-destructive"
												: "text-4xl text-foreground"
									}`}
								>
									{isDnf ? "DNF" : formatTime(entry.elapsedTimeMs)}
								</p>

								{isWinner && (
									<Trophy
										size={24}
										className="text-gold mt-3"
										style={{
											animation: "sparkle 1s ease-in-out infinite",
										}}
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}

// ── Awards Scene ──────────────────────────────────────────────────────

function AwardsScene() {
	return (
		<div className="flex items-center justify-center h-full">
			<div className="text-center">
				<Trophy size={64} className="text-gold mx-auto mb-6" />
				<h2 className="text-5xl text-foreground font-black mb-2">
					Awards Ceremony
				</h2>
				<p className="text-muted-foreground text-xl">
					Congratulations to all our winners!
				</p>
			</div>
		</div>
	);
}

// ── Announcement Scene ────────────────────────────────────────────────

function AnnouncementScene({
	liveScene,
}: {
	liveScene: LiveScene & { scene: "announcement" };
}) {
	return (
		<div className="flex items-center justify-center h-full">
			<div className="text-center max-w-3xl">
				<h2 className="text-6xl text-foreground font-black mb-4">
					{liveScene.message}
				</h2>
				{liveScene.subtitle && (
					<p className="text-2xl text-muted-foreground">{liveScene.subtitle}</p>
				)}
			</div>
		</div>
	);
}
